import L from "leaflet";
import type { LatLngTuple } from "../core/options";
import { snapPoint } from "../geo/snap";
import type { SnapCandidate, SnapResult } from "../geo/snap";
import { drawColor } from "./host";

export const snapRadiusPx = 12;

export type SnapSource = () => SnapCandidate[];

export class Snapper {
  enabled: boolean;

  private readonly map: L.Map;
  private readonly pane: string;
  private readonly reducedMotion: boolean;
  private readonly sources = new Set<SnapSource>();
  private flash: L.CircleMarker | null = null;
  private flashTimer: ReturnType<typeof setTimeout> | null = null;
  private lastFlashKey: string | null = null;

  constructor(map: L.Map, pane: string, enabled: boolean, reducedMotion: boolean, source: SnapSource) {
    this.map = map;
    this.pane = pane;
    this.enabled = enabled;
    this.reducedMotion = reducedMotion;
    this.sources.add(source);
  }

  addSource(source: SnapSource): () => void {
    this.sources.add(source);

    return () => {
      this.sources.delete(source);
    };
  }

  candidates(exclude?: (candidate: SnapCandidate) => boolean): SnapCandidate[] {
    const result: SnapCandidate[] = [];

    for (const source of this.sources) {
      for (const candidate of source()) {
        if (exclude === undefined || !exclude(candidate)) result.push(candidate);
      }
    }

    return result;
  }

  snap(target: LatLngTuple, shiftHeld: boolean, exclude?: (candidate: SnapCandidate) => boolean): SnapResult {
    if (!this.enabled) return { point: target, snapped: false, kind: null, candidate: null, distancePx: Number.POSITIVE_INFINITY };

    const result = snapPoint(this.candidates(exclude), target, this.map, snapRadiusPx, shiftHeld);

    if (result.snapped) this.showFlash(result.point);
    else this.clearFlash();

    return result;
  }

  clearFlash(): void {
    if (this.flashTimer !== null) clearTimeout(this.flashTimer);

    this.flashTimer = null;
    this.lastFlashKey = null;
    this.flash?.remove();
    this.flash = null;
  }

  dispose(): void {
    this.clearFlash();
    this.sources.clear();
  }

  private showFlash(point: LatLngTuple): void {
    const key = `${point[0].toFixed(7)},${point[1].toFixed(7)}`;

    if (key === this.lastFlashKey) return;

    this.clearFlash();
    this.lastFlashKey = key;
    this.flash = L.circleMarker(point, {
      pane: this.pane,
      radius: 9,
      weight: 2,
      color: drawColor,
      fillColor: drawColor,
      fillOpacity: 0.25,
      interactive: false,
      className: this.reducedMotion ? "fxmap-draw-snap-flash fxmap-draw-snap-flash-still" : "fxmap-draw-snap-flash"
    }).addTo(this.map);

    this.flashTimer = setTimeout(() => {
      this.flash?.remove();
      this.flash = null;
      this.flashTimer = null;
    }, this.reducedMotion ? 250 : 450);
  }
}
